/** Section Store video: hydrate media only when it can be watched. */
(function () {
  'use strict';
  const selector = '[data-shoppable-video]';
  const instances = new Map();

  function create(root) {
    const id = root.dataset.shoppableVideo;
    const scope = root.closest('[id^="shopify-section-"]') || root;
    const find = (name) => scope.querySelector(`.${name}-${id}`);
    const list = find('video-slider');
    const modal = find('video-modal');
    const modalList = find('video-modal-slider');
    if (!list || !modal || !modalList) return;
    const cards = [...list.querySelectorAll('[data-shoppable-media]')];
    const videos = [...modalList.querySelectorAll('[data-shoppable-media]')];
    const controller = new AbortController();
    const listen = (el, type, fn, options = {}) => el?.addEventListener(type, fn, { ...options, signal: controller.signal });
    const visible = new Set();
    let slider, modalSlider, observer, nearObserver;
    let opened = false;
    let active = 0;
    let overflow = '';
    let opener;
    let frame = 0;
    let destroyed = false;
    let wasPlaying = false;

    function hydrate(media) {
      if (!media || media.hasAttribute('src')) return;
      if (media.dataset.poster) media.poster = media.dataset.poster;
      if (media.dataset.src) media.src = media.dataset.src;
    }

    function stop(media) {
      if (!media) return;
      if (media.tagName === 'VIDEO') media.pause();
      // Removing an embed stops playback even before its API has finished loading.
      else {
        if (media.hasAttribute('src')) media.removeAttribute('src');
        const poster = media.parentElement.querySelector('[data-shoppable-poster]');
        if (poster) poster.hidden = false;
      }
    }

    function play(media, muted) {
      if (!media) return;
      if (media.tagName === 'VIDEO') {
        media.muted = muted;
        hydrate(media);
        const result = media.play();
        result?.catch(() => {}); // Autoplay policy/network failure leaves native controls usable.
      } else if (!media.hasAttribute('src')) {
        const url = new URL(media.dataset.src);
        url.searchParams.set('autoplay', '1');
        url.searchParams.set(media.dataset.provider === 'youtube' ? 'mute' : 'muted', muted ? '1' : '0');
        media.src = url.href;
        const poster = media.parentElement.querySelector('[data-shoppable-poster]');
        if (poster) poster.hidden = true;
      }
    }

    function syncCards() {
      cards.forEach((media) => {
        const canPlay = !destroyed && !opened && !document.hidden && visible.has(media) && media.dataset.autoplay === 'true';
        if (canPlay) play(media, true);
        else stop(media);
      });
    }

    function initializeList() {
      if (slider || destroyed || typeof window.Swiper !== 'function') return;
      // Hand native preview scrolling over to Swiper before it measures the list.
      list.classList.remove('preview');
      list.scrollLeft = 0;
      slider = new window.Swiper(list, {
        speed: 300,
        slidesPerView: 1.4,
        spaceBetween: Number(list.dataset.gap) || 0,
        navigation: { nextEl: find('video-btn-next'), prevEl: find('video-btn-prev') },
        pagination: { el: find('video-pagination'), clickable: true },
        breakpoints: { 768: { slidesPerView: 3 }, 990: { slidesPerView: 4 }, 1200: { slidesPerView: 5 } },
        on: { transitionEnd: scheduleVisibility },
      });
      nearObserver?.disconnect();
      scheduleVisibility();
    }

    function syncModal() {
      active = modalSlider?.activeIndex ?? active;
      videos.forEach((media, index) => {
        if (opened && !document.hidden && index === active) {
          play(media, false);
          media.closest('.swiper-slide').querySelector(`[data-shoppable-mute]`)?.classList.add('active');
        } else stop(media);
      });
    }

    function open(index, target) {
      opener = target;
      if (!opened) overflow = document.body.style.overflow;
      opened = true;
      active = index;
      modal.hidden = false;
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';
      syncCards();
      if (!modalSlider && typeof window.Swiper === 'function') {
        modalList.querySelectorAll('.swiper-slide').forEach((slide) => { slide.hidden = false; });
        modalSlider = new window.Swiper(modalList, {
          init: false, speed: 300, slidesPerView: 1, spaceBetween: 20, allowTouchMove: false,
          initialSlide: index,
          navigation: {
            nextEl: [...modal.querySelectorAll(`.video-modal-next-${id}`)],
            prevEl: [...modal.querySelectorAll(`.video-modal-prev-${id}`)],
          },
          on: { slideChange: syncModal },
        });
        modalSlider.init();
      } else if (modalSlider) {
        modalSlider.update();
        modalSlider.slideTo(index, 0);
      }
      if (!modalSlider) {
        [...modalList.querySelectorAll('.swiper-slide')].forEach((slide, i) => { slide.hidden = i !== index; });
      }
      syncModal();
      find('video-modal-close')?.focus({ preventScroll: true });
    }

    function close(restoreFocus = true) {
      if (!opened) return;
      opened = false;
      videos.forEach(stop);
      modal.classList.remove('active');
      modal.hidden = true;
      document.body.style.overflow = overflow;
      syncCards();
      if (restoreFocus) opener?.focus({ preventScroll: true });
    }

    // Also used as the fallback for browsers without IntersectionObserver.
    function checkVisibility() {
      frame = 0;
      if (destroyed) return;
      const bounds = list.getBoundingClientRect();
      cards.forEach((media) => {
        const rect = media.getBoundingClientRect();
        const width = Math.min(rect.right, bounds.right, innerWidth) - Math.max(rect.left, bounds.left, 0);
        const height = Math.min(rect.bottom, bounds.bottom, innerHeight) - Math.max(rect.top, bounds.top, 0);
        if (width > 0 && height > 0 && width * height >= rect.width * rect.height * 0.25) visible.add(media);
        else visible.delete(media);
      });
      if (bounds.bottom > -400 && bounds.top < innerHeight + 400) initializeList();
      syncCards();
    }

    function scheduleVisibility() {
      if (!frame && !destroyed) frame = requestAnimationFrame(checkVisibility);
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting, intersectionRatio }) => {
          if (isIntersecting && intersectionRatio >= 0.25) visible.add(target);
          else visible.delete(target);
        });
        syncCards();
      }, { threshold: [0, 0.25] });
      cards.forEach((media) => observer.observe(media));
      nearObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) initializeList();
      }, { rootMargin: '400px 0px' });
      nearObserver.observe(root);
    } else {
      listen(window, 'scroll', scheduleVisibility, { passive: true });
    }
    listen(window, 'resize', scheduleVisibility, { passive: true });
    listen(list, 'scroll', scheduleVisibility, { passive: true });
    listen(list, 'focusin', initializeList);
    listen(list, 'pointerdown', initializeList, { passive: true });
    listen(window, 'load', scheduleVisibility);
    listen(document.querySelector('script[src*="swiper-bundle.min.js"]'), 'load', scheduleVisibility);
    if (root.dataset.lazy === 'false' || window.Shopify?.designMode) initializeList();

    scope.querySelectorAll(`.video-top-${id}`).forEach((top, index) => {
      listen(top, 'click', (event) => {
        if (event.target.closest('a, button, select, input, label')) return;
        open(index, top);
      });
      listen(top, 'keydown', (event) => {
        if (event.target === top && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          open(index, top);
        }
      });
    });
    listen(find('video-modal-close'), 'click', () => close());
    ['prev', 'next'].forEach((direction) => {
      modal.querySelectorAll(`.video-modal-${direction}-${id}`).forEach((button) => listen(button, 'click', () => {
        if (!opened || modalSlider) return;
        const index = Math.max(0, Math.min(videos.length - 1, active + (direction === 'next' ? 1 : -1)));
        if (index !== active) open(index, opener);
      }));
    });
    listen(modal, 'click', (event) => { if (event.target === modal) close(); });
    listen(modal, 'keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key !== 'Tab') return;
      const focusable = [...modal.querySelectorAll('button, a[href], input, select, video[controls], [tabindex="0"]')]
        .filter((el) => !el.disabled && el.getClientRects().length && (!el.closest('.swiper-slide') || el.closest('.swiper-slide') === videos[active]?.closest('.swiper-slide')));
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    videos.forEach((media) => {
      const button = media.closest('.swiper-slide').querySelector('[data-shoppable-mute]');
      listen(button, 'click', () => {
        const unmute = !button.classList.contains('active');
        button.classList.toggle('active', unmute);
        if (media.tagName === 'VIDEO') media.muted = !unmute;
        else if (media.contentWindow && media.hasAttribute('src')) {
          const message = media.dataset.provider === 'youtube'
            ? { event: 'command', func: unmute ? 'unMute' : 'mute', args: [] }
            : { method: 'setVolume', value: unmute ? 1 : 0 };
          media.contentWindow.postMessage(JSON.stringify(message), new URL(media.src).origin);
        }
      });
    });
    listen(document, 'visibilitychange', () => {
      syncCards();
      const media = videos[active];
      if (!opened || !media) return;
      if (document.hidden) {
        wasPlaying = media.tagName === 'IFRAME' ? media.hasAttribute('src') : !media.paused;
        stop(media);
      } else if (wasPlaying) {
        const unmute = media.closest('.swiper-slide').querySelector('[data-shoppable-mute]')?.classList.contains('active');
        play(media, !unmute);
      }
    });

    // Keep product state separate from media visibility and scrolling.
    scope.querySelectorAll('[data-shoppable-product]').forEach((data) => {
      let product;
      try { product = JSON.parse(data.textContent); } catch (_) { return; }
      const block = data.dataset.shoppableProduct;
      const index = data.dataset.index;
      ['', 'modal-'].forEach((prefix) => {
        const selects = [...scope.querySelectorAll(`[data-shoppable-${prefix}select--${block}]`)];
        const input = scope.querySelector(`[data-shoppable-${prefix}input--${index}]`);
        const button = scope.querySelector(`[data-shoppable-${prefix}button--${index}]`);
        if (!selects.length || !input || !button || !product?.variants) return;
        const initial = product.variants.find((variant) => String(variant.id) === input.value);
        if (initial) selects.forEach((select, i) => { select.value = initial.options[i]; });
        selects.forEach((select) => listen(select, 'change', () => {
          const variant = product.variants.find((item) => selects.every((el, i) => el.value === item.options[i]));
          input.value = variant?.id || '';
          button.disabled = !variant?.available;
          const label = button.querySelector('.btn-state-ready');
          if (label) label.textContent = variant?.available ? data.dataset[prefix ? 'modalAvailable' : 'available'] : data.dataset[prefix ? 'modalSoldout' : 'soldout'];
          const price = scope.querySelector(`[data-shoppable-${prefix}price--${index}]`);
          const optionPrice = !prefix && scope.querySelector(`[data-shoppable-option-price--${index}]`);
          const formatter = window.themeVendor?.themeCurrency?.formatMoney;
          const formatted = variant && formatter ? formatter(variant.price, window.theme?.moneyFormat) : '';
          if (price) price.innerHTML = formatted;
          if (optionPrice) optionPrice.innerHTML = formatted;
          scope.querySelectorAll(`[data-shoppable-${prefix}compare--${index}]`).forEach((compare) => {
            const onSale = variant && variant.compare_at_price > variant.price;
            compare.hidden = !onSale;
            compare.innerHTML = onSale && formatter ? formatter(variant.compare_at_price, window.theme?.moneyFormat) : '';
          });
          const image = scope.querySelector(`[data-shoppable-${prefix}image--${index}]`);
          const src = variant?.featured_image?.src || product.featured_image;
          if (image && src) image.src = src;
        }));
      });
    });
    scope.querySelectorAll(`.video-arrow-${id}`).forEach((arrow) => listen(arrow, 'click', () => {
      arrow.closest(`.video-slide-${id}`)?.querySelector(`.video-variants-${id}`)?.classList.toggle('active');
    }));
    scheduleVisibility();
    return {
      refresh: scheduleVisibility,
      destroy() {
        destroyed = true;
        close(false);
        cards.forEach(stop);
        videos.forEach(stop);
        observer?.disconnect();
        nearObserver?.disconnect();
        controller.abort();
        cancelAnimationFrame(frame);
        slider?.destroy(true, true);
        modalSlider?.destroy(true, true);
      },
    };
  }

  function roots(scope) {
    return [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  }
  function scan(scope) {
    roots(scope).forEach((root) => {
      if (instances.has(root)) return;
      const instance = create(root);
      if (instance) instances.set(root, instance);
    });
  }
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => roots(event.target).forEach((root) => {
    instances.get(root)?.destroy();
    instances.delete(root);
  }));
  document.addEventListener('shopify:section:reorder', (event) => roots(event.target).forEach((root) => instances.get(root)?.refresh()));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();
